import { useEffect, useState } from "react";
import { theme, FONT_RUNE } from "./theme";
import { STRATEGIES } from "./strategies";
import { StartingBoardSVG } from "./StartingBoardSVG";
import {
  CaptureRow,
  PieceRow,
  ProgressionDiagram,
  Section,
  StrategyList,
  VictoryRow,
} from "./InstructionsSections";
import { ctaPrimary, ctaSecondary, list, paragraph } from "./InstructionsStyles";
import { useIsWide } from "./useIsWide";

/**
 * Module-scoped cache of the last accordion section the user expanded. Survives modal
 * unmount/remount cycles so reopening (especially from the Tutorial's "?" button) keeps
 * the user's place. Reset on a hard page reload, which is the desired UX.
 */
let lastOpenSection: string | null = null;

export function Instructions({
  open,
  onClose,
  onJumpToGame,
  onWatchTutorial,
}: {
  open: boolean;
  onClose: () => void;
  /** When provided, an "Jump into your own game" CTA is shown in the opening section. */
  onJumpToGame?: () => void;
  /** When provided, a "Watch a guided game" CTA is shown in the opening section. */
  onWatchTutorial?: () => void;
}) {
  const [closing, setClosing] = useState(false);
  /**
   * Accordion: at most one of the bottom sections open at a time. The selected section
   * persists across modal open/close cycles via a module-level cache below — otherwise the
   * Tutorial's narrow-mode "?" reopen would lose the user's place every time.
   */
  const [openSection, setOpenSectionState] = useState<string | null>(lastOpenSection);
  const setOpenSection: typeof setOpenSectionState = (val) => {
    setOpenSectionState((prev) => {
      const next = typeof val === "function" ? (val as (p: string | null) => string | null)(prev) : val;
      lastOpenSection = next;
      return next;
    });
  };
  const toggleSection = (key: string) =>
    setOpenSection((current) => (current === key ? null : key));
  const isWide = useIsWide();

  useEffect(() => {
    if (open) setClosing(false);
  }, [open]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 180);
  };

  const handleJump = () => {
    setClosing(true);
    setTimeout(() => {
      onClose();
      onJumpToGame?.();
    }, 180);
  };

  const handleTutorial = () => {
    setClosing(true);
    setTimeout(() => {
      onClose();
      onWatchTutorial?.();
    }, 180);
  };

  if (!open) return null;

  // On wide viewports, the panel slides in from the left as a full-height drawer.
  // On narrow viewports, it remains a bottom sheet that slides up.
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(8, 5, 3, 0.7)",
    zIndex: 100,
    display: "flex",
    alignItems: isWide ? "stretch" : "flex-end",
    justifyContent: isWide ? "flex-start" : "center",
    backdropFilter: "blur(2px)",
  };

  const panelStyle: React.CSSProperties = isWide
    ? {
        width: "100%",
        maxWidth: 460,
        height: "100%",
        background: theme.bgElev,
        borderRight: `1px solid ${theme.accent}`,
        display: "flex",
        flexDirection: "column",
        transform: closing ? "translateX(-100%)" : "translateX(0)",
        transition: "transform 200ms ease",
        animation: closing ? "" : "rithmoSlideIn 220ms ease",
        boxShadow: "8px 0 22px rgba(0,0,0,0.5)",
        paddingTop: "var(--safe-top)",
        paddingBottom: "var(--safe-bottom)",
      }
    : {
        width: "100%",
        maxWidth: 640,
        maxHeight: "calc(92dvh - var(--safe-bottom))",
        background: theme.bgElev,
        borderTop: `1px solid ${theme.accent}`,
        borderRadius: "10px 10px 0 0",
        display: "flex",
        flexDirection: "column",
        transform: closing ? "translateY(100%)" : "translateY(0)",
        transition: "transform 200ms ease",
        animation: closing ? "" : "rithmoSlideUp 220ms ease",
        paddingBottom: "var(--safe-bottom)",
      };

  return (
    <div onClick={handleClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={panelStyle}>
        <SheetHeader title="How to play" onClose={handleClose} />
        <div style={{ overflowY: "auto", padding: "0 18px 24px", color: theme.parchment }}>
          <Section title="How the game works" defaultOpen>
            <p style={paragraph}>
              <b>The goal</b> is to capture enough enemy pieces to hit an
              agreed point threshold — or, at higher victory levels, to arrange
              your own pieces in mathematical progressions deep in enemy territory.
            </p>
            <p style={paragraph}>
              <b>You accomplish that by</b> advancing pieces toward your
              opponent's half of the 8×16 board. Every piece is a number;
              captures depend on the arithmetic between numbers, not on
              landing on the enemy like in chess.
            </p>

            {(onWatchTutorial || onJumpToGame) && (
              <>
                <p style={{ ...paragraph, marginTop: 14 }}>
                  Watch a game played out with rule explanations from easy to hard
                  and commentary on the strategic thinking — or jump straight into
                  your own game whenever you're ready.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                  {onWatchTutorial && (
                    <button onClick={handleTutorial} style={ctaPrimary}>
                      Watch a guided game →
                    </button>
                  )}
                  {onJumpToGame && (
                    <button onClick={handleJump} style={ctaSecondary}>
                      Jump into your own game →
                    </button>
                  )}
                </div>
              </>
            )}
          </Section>

          <Section
            title="How the board starts"
            open={openSection === "boardStart"}
            onToggle={() => toggleSection("boardStart")}
          >
            <p style={paragraph}>
              Each side has 24 pieces. White (Evens) sits at the top; Black (Odds) sits at the
              bottom. Black moves first. The board is 8 columns × 16 rows.
            </p>
            <StartingBoardSVG />
            <ul style={list}>
              <li>
                <b>White (Evens), 24 pieces</b> — 8 Rounds (2, 4, 6, 8, 4, 16, 36, 64),
                8 Triangles (6, 20, 42, 72, 9, 25, 49, 81), 7 Squares (15, 45, 153, 25, 81, 169, 289),
                plus the White King (Pyramid value 91, six layers: 1, 4, 9, 16, 25, 36) at G4.
              </li>
              <li>
                <b>Black (Odds), 24 pieces</b> — 8 Rounds (3, 5, 7, 9, 9, 25, 49, 81),
                8 Triangles (12, 30, 56, 90, 16, 36, 64, 100), 7 Squares (28, 66, 120, 49, 121, 225, 361),
                plus the Black King (Pyramid value 190, five layers: 16, 25, 36, 49, 64) at A13.
              </li>
            </ul>
          </Section>

          <Section
            title="Pieces and movement"
            open={openSection === "pieces"}
            onToggle={() => toggleSection("pieces")}
          >
            <PieceRow
              shape="●"
              name="Round"
              values="White: 2, 4, 6, 8, 4, 16, 36, 64. Black: 3, 5, 7, 9, 9, 25, 49, 81."
              motion="One step in any direction (orthogonal or diagonal) — eight possible destinations on an open board."
            />
            <PieceRow
              shape="▲"
              name="Triangle"
              values="White: 6, 20, 42, 72, 9, 25, 49, 81. Black: 12, 30, 56, 90, 16, 36, 64, 100."
              motion="Two squares orthogonally — path must be clear. Or a flying knight's hop (1,2 offset) that leaps over any piece. The flying move can only relocate, not capture."
            />
            <PieceRow
              shape="■"
              name="Square"
              values="White: 15, 45, 153, 25, 81, 169, 289. Black: 28, 66, 120, 49, 121, 225, 361."
              motion="Three squares orthogonally — path must be clear. Or a flying extended-knight hop (1,3 offset) that leaps. Flying again can only relocate, never capture."
            />
            <PieceRow
              shape="◆"
              name="King (Pyramid)"
              values="White: 91 (six layers). Black: 190 (five layers)."
              motion="Each turn you declare whether the King moves like a Round, Triangle, or Square — and use that shape's rules. Once every layer of a given shape is captured, the King loses that movement mode."
            />
          </Section>

          <Section
            title="Capture methods"
            open={openSection === "captures"}
            onToggle={() => toggleSection("captures")}
          >
            <CaptureRow
              name="Encounter"
              detail="Your piece is in legal range of an enemy piece with the same value. Declare the capture; the enemy is removed. Optionally advance into the vacated square. Against the enemy King, an Encounter can also match a single layer's value, stripping just that layer."
              example="White Round-6 at B8 reaches Black Round-6 at B9 → capture (Round moves one step in any direction)."
            />
            <CaptureRow
              name="Assault"
              detail="Two of your pieces can both reach the same enemy square, and their values combine by addition, subtraction, multiplication, or division (integer-exact) to equal the enemy's value. Neither attacker moves; the enemy is removed. Against the King, an Assault can also match a single layer's value."
              example="White Rounds 4 and 5 both reach C8 diagonally, where a Black Round-9 sits. 4+5=9 → capture."
            />
            <CaptureRow
              name="Siege (Oblivion)"
              detail="An enemy piece is surrounded on all four orthogonal sides by your pieces or board edges. Triggers automatically the moment the surround completes. Sieges can take the King outright."
              example="A corner piece needs only two attackers; the board edges count as walls."
            />
            <CaptureRow
              name="Deceit"
              detail="Historically a separate capture method with the same conditions as Encounter (matching value, attacker in legal range), but the attacker was required to step into the captured square. We've folded it into Encounter: choosing &ldquo;Advance&rdquo; in the capture panel is the classic Deceit; choosing &ldquo;Hold&rdquo; is the classic Encounter. Same outcome, your choice."
            />
          </Section>

          <Section
            title="Victory conditions"
            open={openSection === "victory"}
            onToggle={() => toggleSection("victory")}
          >
            <p style={paragraph}>
              <b>Common</b> and <b>Proper</b> Victory are scored by what you've captured.
              The three progression-based victories — Greater, Royal, and Glorious — are scored
              by what you've arranged in the <i>triumph zone</i>, the opponent's first five ranks
              (White triumphs in ranks 12–16; Black in ranks 1–5). They also require the enemy
              King to be captured first (whole, or by stripping every layer).
            </p>
            <VictoryRow
              level="Common"
              detail="Your captured pieces total at least the agreed threshold (default 160)."
            />
            <VictoryRow
              level="Proper"
              detail="Reach the threshold with 4+ captured pieces total, spanning at least 2 of the four shapes (Round, Triangle, Square, King). E.g. 3 Rounds + 1 Triangle qualifies; 4 Rounds alone doesn't."
            />
            <VictoryRow
              level="Greater"
              detail="Place three of your pieces in the triumph zone whose values form a single arithmetic, geometric, or harmonic progression."
            />
            <VictoryRow
              level="Royal"
              detail="Place four of your pieces in the triumph zone such that two different progression types can be found among them, each using three of the same four pieces. (One overlapping set of four — not eight pieces.)"
              cta={{
                label: "See progression types →",
                onClick: () => {
                  setOpenSection("progressions");
                  setTimeout(() => {
                    document
                      .getElementById("instructions-section-progressions")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 50);
                },
              }}
            />
            <VictoryRow
              level="Glorious"
              detail="Place four of your pieces in the triumph zone such that all three progression types (arithmetic, geometric, harmonic) can be found among them, each using three of the same four pieces. The canonical set is {3, 4, 6, 9} — only four pieces total, but those four contain an AP {3,6,9}, a GP {4,6,9}, and an HP {3,4,6}."
            />
          </Section>

          <Section
            title="Beginner strategies to try"
            open={openSection === "beginnerStrategies"}
            onToggle={() => toggleSection("beginnerStrategies")}
          >
            <ul style={list}>
              <li>
                March your Rounds toward matching-value enemy Rounds — that sets up the simplest capture (Encounter).
              </li>
              <li>
                Use Triangles' flying knight-hop to slip past your own front line and into enemy ranks — but remember the flying move can't capture; once in range, you'll need the standard 2-orthogonal step to land an Encounter.
              </li>
              <li>
                Don't expose your Pyramid until your other pieces have cleared safe lanes — losing it is a near-decisive blow.
              </li>
            </ul>
          </Section>

          <Section
            id="instructions-section-progressions"
            title="The three progressions"
            open={openSection === "progressions"}
            onToggle={() => toggleSection("progressions")}
          >
            <p style={paragraph}>
              <b>Arithmetic (AP):</b> consecutive terms differ by a constant. <i>3, 6, 9</i> (diffs 3, 3).
            </p>
            <p style={paragraph}>
              <b>Geometric (GP):</b> consecutive terms multiply by a constant ratio. <i>4, 6, 9</i> (ratio 3:2).
            </p>
            <p style={paragraph}>
              <b>Harmonic (HP):</b> the middle term is the harmonic mean. <i>3, 4, 6</i> — equivalently, the reciprocals 1/3, 1/4, 1/6 form an arithmetic progression.
            </p>

            <p style={{ ...paragraph, marginTop: 18 }}>
              <b>Royal and Glorious work by overlap.</b> The same four pieces in the triumph
              zone can hold two (Royal) or all three (Glorious) progressions at once, with each
              progression using a different three of the four. The canonical Glorious set is
              <b> {"{3, 4, 6, 9}"}</b> — four pieces, three triples:
            </p>

            <ProgressionDiagram
              set={[3, 4, 6, 9]}
              triples={[
                { kind: "AP", label: "arithmetic", pieces: [3, 6, 9], check: "diffs 3, 3", color: theme.accentSoft },
                { kind: "GP", label: "geometric", pieces: [4, 6, 9], check: "ratios 3/2, 3/2", color: theme.legalMove },
                { kind: "HP", label: "harmonic", pieces: [3, 4, 6], check: "recip. diffs 1/12, 1/12", color: theme.captureTarget },
              ]}
              caption="Glorious set {3, 4, 6, 9} — every progression shares pieces with the others"
            />

            <p style={paragraph}>
              <b>Alternate Glorious set: <i>{"{4, 6, 9, 12}"}</i></b> — also four pieces, also all three:
            </p>

            <ProgressionDiagram
              set={[4, 6, 9, 12]}
              triples={[
                { kind: "AP", label: "arithmetic", pieces: [6, 9, 12], check: "diffs 3, 3", color: theme.accentSoft },
                { kind: "GP", label: "geometric", pieces: [4, 6, 9], check: "ratios 3/2, 3/2", color: theme.legalMove },
                { kind: "HP", label: "harmonic", pieces: [4, 6, 12], check: "recip. diffs 1/12, 1/12", color: theme.captureTarget },
              ]}
              caption="Alt set {4, 6, 9, 12} — same shape, different pieces"
            />

            <p style={paragraph}>
              <b>Royal Victory</b> works the same way but only needs <i>two</i> of the three
              progressions among the four pieces. Any Glorious set is automatically a Royal
              set; the converse isn't true.
            </p>
          </Section>

          <Section
            title="Common strategies"
            open={openSection === "strategies"}
            onToggle={() => toggleSection("strategies")}
          >
            <StrategyList items={STRATEGIES} />
          </Section>
        </div>
      </div>

      <style>{`
        @keyframes rithmoSlideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes rithmoSlideIn {
          from { transform: translateX(-100%); }
          to   { transform: translateX(0); }
        }
        details > summary { list-style: none; cursor: pointer; }
        details > summary::-webkit-details-marker { display: none; }
      `}</style>
    </div>
  );
}

function SheetHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 18px 10px",
        borderBottom: `1px solid ${theme.boardEdge}`,
        position: "sticky",
        top: 0,
        background: theme.bgElev,
        zIndex: 1,
      }}
    >
      <div className="rune" style={{ fontSize: 18, color: theme.parchment, letterSpacing: 1 }}>
        {title}
      </div>
      <button
        onClick={onClose}
        style={{
          fontSize: 20,
          padding: "4px 12px",
          background: theme.boardEdge,
          color: theme.parchment,
          border: `1px solid ${theme.inkSoft}`,
          borderRadius: 6,
          fontFamily: FONT_RUNE,
          cursor: "pointer",
          lineHeight: 1,
        }}
        aria-label="Close instructions"
      >
        ×
      </button>
    </div>
  );
}

